define('viewModels/BreadcrumbNav',[
    'viewModels/ViewModel',
    'util/ObservableModel',
    'lib/knockout',
    'lib/lodash4',
    'util/codeUtils',
    'services/navHelper',
    'services/embeddingService',

    'extensions/i18n/knockout.i18n'
], function (ViewModel, ObservableModel, ko, _, codeUtils, navHelper, embeddingService) {
    'use strict';

    /**
     * A ViewModel for the breadcrumb navigation bar.
     *
     * For documentation on how to use this viewmodel, please take a look at the following
     * wiki page: https://bitbucket.org/inindca/pure-cloud-browser-app/wiki/Breadcrumb_Navigation
     *
     * # Optional Arguments
     *
     * @param {Object} [labelData={}] An object containing a set of key-value pairs for
     *  label data in the breadcrumb. Keys should be the name of breadcrumb as it is layed
     *  out in the sitemap definition (see sitemap.js). The value for a given key should
     *  be an object that contains key-value pairs that will be used to render tokens in
     *  an externalized string.
     *
     *  For example, for a sitemap entry of "fooList" and an externalized string of:
     *
     *      "siteMap.fooList" = "Bar {{barToken}}",
     *
     *  Then the argument should be:
     *
     *      {
     *          fooList: {
     *              barToken: "label"
     *          }
     *      }
     */
    var BreadcrumbNav = ViewModel.extend(function (routeBreadcrumbs, opts) {
        var options = _.assign({}, {
            labelData: {}
        }, opts);

        var self = this;

        var breadcrumbs = self.computed(function () {
            var labelData = options.labelData;

            return _.map(routeBreadcrumbs, function (bcObj) {
                var bc = _.assign({}, {
                    // add a data attribute to account for templates in the externalized
                    // string
                    labelData: {}
                }, bcObj);

                // only replace the label's value if there is not already a key in the
                // externalized strings for it.
                if (_.has(labelData, bc.key)) {
                    var data = labelData[bc.key];
                    bc.labelData = data;
                }

                return bc;
            });
        });

        var length = self.computed(function () {
            if (!breadcrumbs()) {
                return 0;
            }

            return breadcrumbs().length;
        });

        var lastIndex = self.computed(function () {
            return length() - 1;
        });

        /**
         * If there are any breadcrumbs to display.
         */
        self.hasAny = self.computed(function () {
            return length() > 0;
        });

        /**
         * The home breadcrumb.
         */
        self.getFirst = self.computed(function () {
            if (length() > 0) {
                return breadcrumbs()[0];
            }

            return null;
        });

        /**
         * If there is so many breadcrumbs the dropdown menu is needed..
         */
        self.hasMenu = self.computed(function () {
            return length() > 3;
        });

        /**
         * A list of all breadcrumbs between the last and the first.
         */
        self.getOthers = self.computed(function () {
            if (length() > 3) {
                return breadcrumbs();
            }

            return null;
        });

        /**
         * The last breadcrumb.
         */
        self.getPrevious = self.computed(function () {
            if (length() > 2) {
                return breadcrumbs()[lastIndex() - 1];
            }

            return null;
        });

        /**
         * The most current breadcrumb.
         */
        self.getCurrent = self.computed(function () {
            if (length() > 1) {
                return breadcrumbs()[lastIndex()];
            }

            return null;
        });

        /**
         * The array needed for the embedded app
         */
        self.getAllEmbedded = function () {
            var crumbs = _.clone(breadcrumbs());

            // remove home link
            if (crumbs.length > 0 && crumbs[0].key === "home") {
                crumbs.shift();
            }

            // make campaigns dashboard a first level breadcrumb
            if (crumbs.length > 1 && crumbs[1].key === "outboundCampaigns") {
                crumbs.shift();
            }

            // localize strings
            _.map(crumbs, function (crumb) {
                if (!_.isUndefined(crumb.labelData.pageTitle)) {
                    crumb.value = ko.unwrap(crumb.labelData.pageTitle);
                } else {
                    crumb.value = _.unescape(ko.i18n.renderString(crumb.label, crumb.labelData));
                }
                crumb.class = self.createCSSClass(crumb.label);
            });

            // set active item
            if (crumbs.length > 0) {
                crumbs[crumbs.length - 1].active = true;
            }

            // adjust hash for embedded app
            _.map(crumbs, function (crumb) {
                if (crumb.hash) {
                    crumb.hash = crumb.hash.replace('#', '#/engage/');
                }
            });

            embeddingService.notifyNewBreadCrumbs(_.map(crumbs, function (crumb) {
                return _.pick(crumb, ["active","class","value","hash", "alternateRoute"]);
            }));

            return crumbs;
        };

        /**
         * The dropdown menu state.
         */
        self.isMenuOpen = ko.observable(false);

        /**
         * Create classes for breadcrumb items
         */
        self.createCSSClass = _.partial(codeUtils.generateClassFromString, "breadcrumb-");

        /**
         * The overflow menu should close automatically when navigating to another page without requiring a second
         * click outside of the menu to trigger the bootstrap menu closing logic.
         */
        self.clickMenuItem = function (item) {
            if (!item.hash) {
                return;
            }
            self.isMenuOpen(false);
            navHelper.navigateTo(item.hash);
        };
    });

    return BreadcrumbNav;
});

